import { useRouter, usePathname, useSearchParams, useParams } from 'next/navigation';
import { useIntl } from 'react-intl';
import { Chip } from '@packages/shared/src/components/Chip/Chip';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useIsBot } from '@packages/tracking/src/hooks/useIsBot/useIsBot';
import { getFragmentData, type FragmentType } from '@packages/gql/generated/shopping';
import { RatingTagFragmentFragmentDoc } from '@packages/gql/generated/shopping/RatingTagFragmentFragmentDoc';
import { RatingTagSkuFragmentFragmentDoc } from '@packages/gql/generated/shopping/RatingTagSkuFragmentFragmentDoc';
import { ratingDetailPagePath, ratingFilterTagPrefix } from '../config';
import { useRatingTracking } from '../../Tracking/hooks/useRatingTracking';
import type { CustomerRatingsParams } from '../types';
import { getSearchParamsUrlSuffix } from '../helper/getSearchParamsUrlSuffix';
import { ratingTagMessaages } from './messages';

/* GraphQL */ `
  fragment RatingTagFragment on RatingTag {
    ratingCount
    tag {
      id
      key
      name
    }
  }
`;

/* GraphQL */ `
  fragment RatingTagSkuFragment on Product {
    sku
  }
`;

export type RatingTagProps = {
  tagData: FragmentType<typeof RatingTagFragmentFragmentDoc>;
  /**
   * sku needed for product in rating page
   */
  skuData: FragmentType<typeof RatingTagSkuFragmentFragmentDoc>;
  /**
   * category for glycerin tracking
   */
  category: 'Vorteile' | 'Nachteile';
};

export const RatingTag = ({ tagData, skuData, category }: RatingTagProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = useParams<CustomerRatingsParams>();
  const { push } = useRouter();
  const isBot = useIsBot();
  const { language, localizeUrl } = useI18n();
  const { formatMessage } = useIntl();

  const { trackGlycerinButtonClick } = useRatingTracking();

  if (!tagData) return null;

  const {
    ratingCount,
    tag: { id, key, name },
  } = getFragmentData(RatingTagFragmentFragmentDoc, tagData);
  const { sku } = getFragmentData(RatingTagSkuFragmentFragmentDoc, skuData);

  const filterTag = `${ratingFilterTagPrefix}${key}`;
  const isActive = params?.ftag ? params.ftag[0] === filterTag : false;

  const onClickHandler = () => {
    if (key && !isBot && pathname !== null && params !== null) {
      trackGlycerinButtonClick({
        category,
        detail: name,
      });
      push(
        localizeUrl(
          `/p/${params.akl ?? params.slug}/rating${isActive ? '' : `/${filterTag}`}${getSearchParamsUrlSuffix(searchParams, sku)}`,
          language,
        ),
        { scroll: !pathname.includes(ratingDetailPagePath) },
      );
    }
  };

  return (
    <Chip
      aria-label={formatMessage(ratingTagMessaages.chipLabel, { name, ratingCount })}
      key={id}
      component="li"
      label={`${name} (${ratingCount})`}
      color={isActive ? 'primary' : 'default'}
      onClick={onClickHandler}
    />
  );
};
